import React, { useState, useMemo } from 'react';
import type { ApplicantDetailsData } from '../../../types/customer-portal';
import { validateNameField, filterNameField, NAME_FIELD_MAX_LENGTH } from '../../../../../shared/utils/fieldValidation';
import './ApplicantDetailsStep.css';

// Field validation highlighting interface
interface FieldValidationErrors {
  [key: string]: boolean;
}

interface ApplicantDetailsStepProps {
  data?: ApplicantDetailsData;
  onNext: (data: ApplicantDetailsData) => void;
  onBack: () => void;
}

const ApplicantDetailsStep: React.FC<ApplicantDetailsStepProps> = ({
  data,
  onNext,
  onBack
}) => {
  const [formData, setFormData] = useState<ApplicantDetailsData>({
    title: data?.title || '',
    firstName: data?.firstName || '',
    middleName: data?.middleName || '',
    lastName: data?.lastName || '',
    suffix: data?.suffix || 'N/A'
  });

  // Field validation highlighting state
  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Form validation logic - check required fields
  const isFormValid = useMemo(() => {
    const requiredFields: (keyof ApplicantDetailsData)[] = [
      'title',
      'firstName',
      'lastName'
    ];

    // Check if all required fields are filled
    return requiredFields.every(field => {
      const value = formData[field];
      return value && value.trim() !== '';
    });
  }, [formData]);

  // Get validation errors for required fields
  const getValidationErrors = (): FieldValidationErrors => {
    const errors: FieldValidationErrors = {};
    
    // Required fields based on task requirements
    if (!formData.title?.trim()) errors.title = true;
    if (!formData.firstName?.trim()) errors.firstName = true;
    if (!formData.lastName?.trim()) errors.lastName = true;
    
    // Add pattern validation for name fields
    if (formData.firstName && !validateNameField(formData.firstName)) errors.firstName = true;
    if (formData.lastName && !validateNameField(formData.lastName)) errors.lastName = true;
    if (formData.middleName && !validateNameField(formData.middleName)) errors.middleName = true;
    
    return errors;
  };

  // Helper function to get field error class
  const getFieldErrorClass = (fieldName: string): string => {
    return showValidationErrors && fieldErrors[fieldName] ? 'field-error' : '';
  };

  // Helper function to clear field error when user starts typing
  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleInputChange = (field: keyof ApplicantDetailsData, value: string) => {
    let processedValue = value;
    
    // Apply field-specific filtering for name fields
    if (field === 'firstName' || field === 'lastName' || field === 'middleName') {
      processedValue = filterNameField(value);
    }
    
    // Clear field error when user starts typing
    clearFieldError(field);
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));
  };

  const handleNext = () => {
    // Check for validation errors
    const validationErrors = getValidationErrors();
    const hasErrors = Object.keys(validationErrors).length > 0;
    
    if (hasErrors) {
      // Show validation errors by highlighting fields
      setFieldErrors(validationErrors);
      setShowValidationErrors(true);
      return; // Don't proceed if there are validation errors
    }
    
    // Clear any previous validation errors
    setFieldErrors({});
    setShowValidationErrors(false);
    
    // Proceed with existing logic
    if (isFormValid) {
      onNext(formData);
    }
  };

  return (
    <>
    <div className="applicant-details-step">
      <div className="step-header">
        <h3 className="step-title">Applicant Details</h3>
        <p className="step-description">A person who will buy a travel insurance for someone else.</p>
      </div>

      <div className="step-form">
        {/* Name Fields Row */}
        <div className="form-row name-row">
          <div className={`form-group title-group ${getFieldErrorClass('title')}`}>
            <label className="form-label">Title *</label>
            <select
              className="form-select"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
            >
              <option value="">Title</option>
              <option value="Ms.">Ms.</option>
              <option value="Mrs.">Mrs.</option>
              <option value="Mr.">Mr.</option>
            </select>
          </div>

          <div className={`form-group ${getFieldErrorClass('firstName')}`}>
            <label className="form-label">First Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder=""
              maxLength={NAME_FIELD_MAX_LENGTH}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Middle Name</label>
            <input
              type="text"
              className="form-input"
              value={formData.middleName}
              onChange={(e) => handleInputChange('middleName', e.target.value)}
              placeholder=""
              maxLength={NAME_FIELD_MAX_LENGTH}
            />
          </div>

          <div className={`form-group ${getFieldErrorClass('lastName')}`}>
            <label className="form-label">Last Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder=""
              maxLength={NAME_FIELD_MAX_LENGTH}
            />
          </div>

          <div className="form-group suffix-group">
            <label className="form-label">Suffix</label>
            <select
              className="form-select"
              value={formData.suffix}
              onChange={(e) => handleInputChange('suffix', e.target.value)}
            >
              <option value="N/A">N/A</option>
              <option value="Jr.">Jr.</option>
              <option value="Sr.">Sr.</option>
              <option value="II">II</option>
              <option value="III">III</option>
            </select>
          </div>
        </div>
      </div>
    </div>
      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button
          type="button"
          className="btn-back"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className="btn-next"
          onClick={handleNext}
        >
          Next
        </button>
      </div>    
</>    
  );
};

export default ApplicantDetailsStep;